import { Descope, useSession } from '@descope/react-sdk'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useReportErrorsCallback } from '@/lib/connect/error-reporting'
import { LocalStorageKeys, useLocalStorage } from '@/lib/storage/local-storage.hook'

import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

export type LoginPageProps = {
  authenticatedPath: string
  descopeFlowId?: string
}

export const LoginPage = ({
  authenticatedPath,
  descopeFlowId = 'sign-up-or-in',
}: LoginPageProps) => {
  const { isAuthenticated } = useSession()
  const navigate = useNavigate()
  const location = useLocation()

  const [storedRedirectPath, setStoredRedirectPath] = useLocalStorage(
    LocalStorageKeys.loginRedirectPath,
  )

  // Initialize and persist redirect path
  const [toPath, setToPath] = useState(authenticatedPath)

  useEffect(() => {
    if (location.state?.from) {
      const redirectPath = getPathFromState(location.state.from)
      setStoredRedirectPath(redirectPath)
      // oxlint-disable-next-line react/set-state-in-effect
      setToPath(redirectPath)
    } else if (storedRedirectPath) {
      // oxlint-disable-next-line react/set-state-in-effect
      setToPath(storedRedirectPath)
    }
  }, [location.state, storedRedirectPath, setStoredRedirectPath])

  useEffect(() => {
    if (isAuthenticated) {
      // Clear the stored redirect path after successful login
      setStoredRedirectPath('')
      navigate(toPath)
    }
  }, [isAuthenticated, navigate, toPath, setStoredRedirectPath])

  const reportErrors = useReportErrorsCallback()
  const { toast } = useToast()
  return (
    <div className='mx-auto mt-16 max-w-lg'>
      {!isAuthenticated && (
        <Descope
          flowId={descopeFlowId}
          theme='light'
          onError={(err) => {
            reportErrors(new Error(`Descope error ${err}`))
            toast({
              title: 'Error',
              description: 'An error occurred. Please try again later.',
              variant: 'destructive',
            })
          }}
        />
      )}
    </div>
  )
}

const getPathFromState = ({
  pathname,
  search,
  hash,
}: {
  pathname: string
  search: string
  hash: string
}) => {
  return `${pathname}${search}${hash}`
}
